import { FC, FocusEvent, memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Stack, useTheme } from "@mui/material";
import {
  IRoadLayoutConfig,
  IRoadwayConfig,
  IRoadwayConfigCallback,
  RoadCoveringEnum,
  BorderAreaCoveringEnum,
  CarriageWayEnum,
  q0Enum,
  IOptimizationConfig,
  IOptimizationConfigCallBack,
  TooltipData,
} from "types";
import { setObjectURL } from "utils/urls";
import { Section } from "components/Section";
import { Row } from "components/Row";
import { ToggleGroup } from "components/ToggleGroup";
import { NumberField } from "components/NumberField";
import { InputField } from "components/InputField";
import { CarriagewayImage } from "components/CarriagewayImage";
import BorderArea from "./components/BorderArea";
import getStyles from "./utils/styles";

type Props = {
  roadLayoutConfig: IRoadLayoutConfig;
  roadwayConfig: IRoadwayConfig;
  tooltips: TooltipData | null;
  onRoadwayConfig: IRoadwayConfigCallback;
  optimizationConfig?: IOptimizationConfig;
  onOptimizationConfig?: IOptimizationConfigCallBack;
};

const inputFieldMap: Record<string, keyof IRoadwayConfig> = {
  road_width: "roadWidth",
  road_width_central_reservation: "widthOfCentralReservation",
  number_of_lanes: "numberOfLanes",
  road_width_border_area_right: "borderAreaRightWidth",
  kerb_distance_right: "borderAreaRightKerbDistance",
  road_width_border_area_left: "borderAreaLeftWidth",
  kerb_distance_left: "borderAreaLeftKerbDistance",
  road_length: "roadLength",
};

const coveringFieldMap: Record<string, "covering" | "q0" | "borderAreaRightCovering" | "borderAreaLeftCovering"> = {
  RoadCovering: "covering",
  q0: "q0",
  BorderAreaRightCovering: "borderAreaRightCovering",
  BorderAreaLeftCovering: "borderAreaLeftCovering",
};

const roadCoveringOptions = [
  { label: "R1", value: RoadCoveringEnum.R1 },
  { label: "R2", value: RoadCoveringEnum.R2 },
  { label: "R3", value: RoadCoveringEnum.R3 },
  { label: "R4", value: RoadCoveringEnum.R4 },
  { label: "C1", value: RoadCoveringEnum.C1 },
  { label: "C2", value: RoadCoveringEnum.C2 },
];

const q0Options = [
  { label: "0.05", value: q0Enum.q0005 },
  { label: "0.07", value: q0Enum.q0007 },
  { label: "0.08", value: q0Enum.q0008 },
  { label: "0.09", value: q0Enum.q0009 },
  { label: "0.1", value: q0Enum.q0010 },
];

const RoadWay: FC<Props> = ({ roadLayoutConfig, roadwayConfig, onRoadwayConfig, tooltips }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme).RoadWay, [theme]);

  const { carriageWay, borderAreaLeft, borderAreaRight } = roadLayoutConfig;
  console.log("RoadWay -> roadLayoutConfig", roadwayConfig);

  const {
    roadWidth,
    widthOfCentralReservation,
    numberOfLanes,
    covering,
    q0,
    borderAreaRightWidth,
    borderAreaRightKerbDistance,
    borderAreaLeftWidth,
    borderAreaLeftKerbDistance,
    roadLength,
  } = roadwayConfig;

  const handleInput = useCallback(
    (name: string, value: number): void => {
      const field = inputFieldMap[name];
      if (!field) return;

      const tmpValue = { ...roadwayConfig, [field]: value };
      onRoadwayConfig(tmpValue);
      setObjectURL(tmpValue);
    },
    [onRoadwayConfig, roadwayConfig]
  );

  const handleRoadCovering = useCallback(
    (classType: string, value: RoadCoveringEnum | BorderAreaCoveringEnum | q0Enum): void => {
      const field = coveringFieldMap[classType];
      if (!field) return;

      const result = { ...roadwayConfig, [field]: value } as IRoadwayConfig;
      onRoadwayConfig(result);
      setObjectURL(result);
    },
    [onRoadwayConfig, roadwayConfig]
  );

  const handleRoadLengthFocus = useCallback((e: FocusEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    input.value = input.value === "0" ? "" : input.value;
  }, []);

  return (
    <Stack sx={styles.container}>
      <CarriagewayImage roadLayoutConfig={roadLayoutConfig} sx={styles.image} />

      <Section title={t("Roadway")} sx={styles.roadway}>
        <Box sx={styles.grid}>
          <NumberField
            name="road_width"
            value={roadWidth}
            label={`${t("Width")} (m)`}
            min={0.01}
            max={100}
            precision={2}
            step={0.5}
            helperText={t("validation_sentence", { min: 0.01, max: 100 })}
            onChange={handleInput}
          />
          {carriageWay === CarriageWayEnum.DOUBLE && (
            <NumberField
              name="road_width_central_reservation"
              value={widthOfCentralReservation}
              label={`${t("Width Of Central Reservation")} (m)`}
              min={0}
              max={100}
              precision={2}
              step={0.5}
              helperText={t("validation_sentence", { min: 0.01, max: 100 })}
              tooltip={tooltips?.roadway_wocr}
              onChange={handleInput}
            />
          )}
          <NumberField
            name="number_of_lanes"
            value={numberOfLanes}
            label={t("Number Of Lanes")}
            min={1}
            max={6}
            precision={0}
            step={1}
            helperText={t("validation_sentence", { min: 1, max: 6 })}
            onChange={handleInput}
          />
          <InputField
            type="number"
            name="road_length"
            value={roadLength}
            label={`${t("Road length")} (km)`}
            placeholder="Example: 2 or 0.5"
            onChange={handleInput as (name: string, value: string | number) => void}
            onFocus={handleRoadLengthFocus}
          />
        </Box>

        <Row>
          <ToggleGroup
            radio
            name="RoadCovering"
            label={t("Covering")}
            help={tooltips?.roadway_cover || t("Reflectance tables")}
            selected={covering}
            onChange={handleRoadCovering as (name: string, value: string) => void}
            options={roadCoveringOptions}
          />
          <ToggleGroup
            radio
            name="q0"
            label="q0"
            help={tooltips?.roadway_qzero || t("Average luminance coefficient")}
            selected={q0}
            onChange={handleRoadCovering as (name: string, value: string) => void}
            options={q0Options}
          />
        </Row>
      </Section>

      {(borderAreaLeft || borderAreaRight) && (
        <Stack sx={styles.borders}>
          {borderAreaLeft && <BorderArea side="left" areaWidth={borderAreaLeftWidth} kerbDistance={borderAreaLeftKerbDistance} onInput={handleInput} />}
          {borderAreaRight && <BorderArea side="right" areaWidth={borderAreaRightWidth} kerbDistance={borderAreaRightKerbDistance} onInput={handleInput} />}
        </Stack>
      )}
    </Stack>
  );
};

RoadWay.displayName = "RoadWay";

export default memo(RoadWay);
